import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Sidebar from '../../components/Sidebar/Sidebar';
import { User } from '../../types/index';
import styles from './UserDetails.module.scss';

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('General Details');

  const tabs = [
    'General Details',
    'Documents',
    'Bank Details',
    'Loans',
    'Savings',
    'App and System',
  ];

  // Route protection
  useEffect(() => {
    const auth = localStorage.getItem('lendsqr_auth');
    if (!auth) {
      navigate('/login');
      return;
    }

    // Load user from localStorage
    const stored = localStorage.getItem('lendsqr_user');
    if (stored) {
      const parsedUser: User = JSON.parse(stored);
      setUser(parsedUser);
    } else {
      navigate('/users');
    }
  }, [navigate, id]);

  const handleBlacklist = () => {
    if (!user) return;
    const updated = { ...user, status: 'blacklisted' as User['status'] };
    setUser(updated);
    localStorage.setItem('lendsqr_user', JSON.stringify(updated));
  };

  const handleActivate = () => {
    if (!user) return;
    const updated = { ...user, status: 'active' as User['status'] };
    setUser(updated);
    localStorage.setItem('lendsqr_user', JSON.stringify(updated));
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
    })}`;
  };

  const renderStars = (tier: number) => {
    return [1, 2, 3].map((star) => (
      <span
        key={star}
        className={`${styles.star} ${star <= tier ? styles.filled : ''}`}
      >
        ★
      </span>
    ));
  };

  const renderGeneralDetails = () => {
    if (!user) return null;

    return (
      <>
        {/* Personal Information */}
        <div className={styles.detailsSection}>
          <h3 className={styles.sectionTitle}>Personal Information</h3>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Full Name</label>
              <p>{user.firstName} {user.lastName}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Phone Number</label>
              <p>{user.phone}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Email Address</label>
              <p>{user.email}</p>
            </div>
            <div className={styles.detailItem}>
              <label>BVN</label>
              <p>{user.bvn}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Gender</label>
              <p>{user.gender}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Marital Status</label>
              <p>{user.maritalStatus}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Children</label>
              <p>{user.children}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Type of Residence</label>
              <p>{user.residenceType}</p>
            </div>
          </div>
        </div>

        {/* Education and Employment */}
        <div className={styles.detailsSection}>
          <h3 className={styles.sectionTitle}>Education and Employment</h3>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Level of Education</label>
              <p>{user.educationLevel}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Employment Status</label>
              <p>{user.employmentStatus}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Sector of Employment</label>
              <p>{user.employmentSector}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Duration of Employment</label>
              <p>{user.employmentDuration}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Office Email</label>
              <p>{user.officeEmail}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Monthly Income</label>
              <p>{formatCurrency(user.monthlyIncome)}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Loan Repayment</label>
              <p>{formatCurrency(user.loanRepayment)}</p>
            </div>
          </div>
        </div>

        {/* Socials */}
        <div className={styles.detailsSection}>
          <h3 className={styles.sectionTitle}>Socials</h3>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Twitter</label>
              <p>{user.twitter}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Facebook</label>
              <p>{user.facebook}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Instagram</label>
              <p>{user.instagram}</p>
            </div>
          </div>
        </div>

        {/* Guarantor */}
        <div className={styles.detailsSection}>
          <h3 className={styles.sectionTitle}>Guarantor</h3>

          {/* Guarantor 1 */}
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Full Name</label>
              <p>{user.guarantor1FirstName} {user.guarantor1LastName}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Phone Number</label>
              <p>{user.guarantor1Phone}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Email Address</label>
              <p>{user.guarantor1Email}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Relationship</label>
              <p>{user.guarantor1Relationship}</p>
            </div>
          </div>

          {/* Guarantor 2 */}
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Full Name</label>
              <p>{user.guarantor2FirstName} {user.guarantor2LastName}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Phone Number</label>
              <p>{user.guarantor2Phone}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Email Address</label>
              <p>{user.guarantor2Email}</p>
            </div>
            <div className={styles.detailItem}>
              <label>Relationship</label>
              <p>{user.guarantor2Relationship}</p>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderTabContent = () => {
    if (activeTab === 'General Details') {
      return renderGeneralDetails();
    }

    return (
      <div className={styles.detailsSection}>
        <div className={styles.comingSoon}>
          <svg width="60" height="60" fill="none" viewBox="0 0 24 24" stroke="#8B9AB7" strokeWidth={1}>
            <path strokeLinecap="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p>No data available</p>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className={styles.userDetailsLayout}>
        <Sidebar isOpen={sidebarOpen} />
        <div className={styles.mainContent}>
          <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.userDetailsLayout}>
      <Sidebar isOpen={sidebarOpen} />
      <div className={styles.mainContent}>
        <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className={styles.pageContent}>

          {/* Back Button */}
          <button
            className={styles.backBtn}
            onClick={() => navigate('/users')}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Users
          </button>

          {/* Page Header */}
          <div className={styles.pageHeader}>
            <h1>User Details</h1>
            <div className={styles.headerActions}>
              <button
                className={styles.btnBlacklist}
                onClick={handleBlacklist}
              >
                Blacklist User
              </button>
              <button
                className={styles.btnActivate}
                onClick={handleActivate}
              >
                Activate User
              </button>
            </div>
          </div>

          {/* Profile Card */}
          <div className={styles.profileCard}>
            <div className={styles.profileTop}>

              {/* Avatar */}
              <div className={styles.avatar}>
                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
              </div>

              {/* Name */}
              <div className={styles.profileInfo}>
                <h2>{user.firstName} {user.lastName}</h2>
                <p>{user.username}</p>
              </div>

              <div className={styles.divider} />

              {/* Tier */}
              <div className={styles.tierSection}>
                <h4>User's Tier</h4>
                <div className={styles.stars}>
                  {renderStars(user.tier)}
                </div>
              </div>

              <div className={styles.divider} />

              {/* Balance */}
              <div className={styles.balanceSection}>
                <h3>{formatCurrency(user.accountBalance)}</h3>
                <p>{user.accountNumber}/{user.bank}</p>
              </div>

            </div>

            {/* Tabs */}
            <div className={styles.profileTabs}>
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {renderTabContent()}

        </div>
      </div>
    </div>
  );
};

export default UserDetails;